import { useState } from "react";
import { ShieldCheck, ShieldAlert, AlertTriangle, RotateCcw, Check } from "lucide-react";
import { getAccountRuntimeTruth } from "../lib/accountRuntime";
import { post } from "../lib/api";

function Bar({ label, value, max, isPercent = true, testId }) {
  const numericVal = Number(value || 0);
  const numericMax = Number(max || 1);
  const pct = Math.min(100, Math.max(0, (numericVal / numericMax) * 100 || 0));
  
  // Dynamic gradient based on threshold
  const barStyle = pct < 50 
    ? "from-emerald-500 to-teal-400 shadow-emerald-500/20" 
    : pct < 80 
    ? "from-amber-500 to-yellow-400 shadow-amber-500/20" 
    : "from-rose-600 to-red-500 shadow-rose-500/30 animate-pulse";

  const formattedVal = isPercent ? `${numericVal.toFixed(2)}%` : Math.round(numericVal);
  const formattedMax = isPercent ? `${numericMax.toFixed(2)}%` : Math.round(numericMax);

  return (
    <div data-testid={testId} className="space-y-1">
      <div className="flex justify-between text-[11px] font-mono">
        <span className="uppercase tracking-wider font-semibold text-zinc-400">{label}</span>
        <span className="font-bold text-zinc-200">{formattedVal} <span className="text-zinc-600 font-normal">/ {formattedMax}</span></span>
      </div>
      <div className="h-2 bg-zinc-900 rounded-full border border-zinc-800/80 p-0.5 overflow-hidden shadow-inner">
        <div 
          className={`h-full rounded-full bg-gradient-to-r ${barStyle} transition-all duration-500 ease-out shadow-sm`} 
          style={{ width: `${pct}%` }} 
        />
      </div>
    </div>
  );
}

export default function RiskPanel({ risk, account, status, selectedViewAccountId, onRefresh }) {
  const [resetting, setResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  if (!risk) return null;

  const runtimeTruth = getAccountRuntimeTruth(status || { risk });
  const isHalted = runtimeTruth.riskLocked;
  const isStreakPaused = Boolean(risk.entry_pause);

  const handleResetStreak = async () => {
    setResetting(true);
    try {
      const accountId = selectedViewAccountId || status?.exchange?.account_id || "paper";
      await post(`/accounts/${encodeURIComponent(accountId)}/reset-loss-streak`);
      setResetSuccess(true);
      if (onRefresh) onRefresh();
      setTimeout(() => setResetSuccess(false), 3000);
    } catch (err) {
      console.error("Failed to reset loss streak:", err);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="bg-zinc-950/90 border border-zinc-800/80 p-4 space-y-3.5 rounded-xl shadow-2xl backdrop-blur-md" data-testid="risk-panel">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-zinc-800/70 pb-3">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center border ${
            isHalted 
              ? "bg-rose-500/10 border-rose-500/30 text-rose-400 animate-pulse" 
              : isStreakPaused
              ? "bg-amber-500/10 border-amber-500/30 text-amber-400 animate-pulse"
              : "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
          }`}>
            {isHalted ? <ShieldAlert size={15} /> : isStreakPaused ? <AlertTriangle size={15} /> : <ShieldCheck size={15} />}
          </div>
          <div>
            <h3 className="text-xs uppercase tracking-wider font-bold text-zinc-100 font-mono">Risk Manager</h3>
            <p className="text-[10px] text-zinc-500 font-mono">Real-time exposure & drawdown guard</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span
            role="status"
            aria-label={`Risk manager ${isHalted ? "halted" : isStreakPaused ? "paused" : "active"}`}
            className={`text-[10px] font-mono px-2.5 py-1 rounded-md font-bold uppercase tracking-wider border flex items-center gap-1.5 shadow-sm ${
              isHalted
                ? "border-rose-700 bg-rose-950/80 text-rose-400 shadow-rose-950"
                : isStreakPaused
                ? "border-amber-700 bg-amber-950/80 text-amber-400 shadow-amber-950"
                : "border-emerald-800 bg-emerald-950/80 text-emerald-400 shadow-emerald-950"
            }`}
            data-testid="kill-switch-status"
          >
            <span className={`w-1.5 h-1.5 rounded-full ${isHalted ? "bg-rose-500 animate-ping" : isStreakPaused ? "bg-amber-400 animate-ping" : "bg-emerald-400"}`} />
            {isHalted ? "HALTED" : isStreakPaused ? "PAUSED (STREAK)" : "ACTIVE"}
          </span>
        </div>
      </div>

      {isHalted && (
        <p className="text-[10px] text-rose-300 font-mono bg-rose-950/40 p-2 rounded border border-rose-900/60" data-testid="risk-lock-reason">
          {runtimeTruth.executionDetail}. Use the selected account recovery controls in Settings.
        </p>
      )}

      {isStreakPaused && !isHalted && (
        <div className="p-2.5 rounded-lg bg-amber-950/40 border border-amber-500/50 flex flex-wrap items-center justify-between gap-2 text-amber-200">
          <div className="flex items-center gap-2 text-xs font-mono">
            <AlertTriangle size={14} className="text-amber-400 shrink-0" />
            <div>
              <div className="font-bold uppercase text-[10px] text-amber-300 tracking-wider">Loss Streak Protection Active</div>
              <div className="text-[10px] text-amber-200/90">{risk.entry_pause_reason || `${risk.consecutive_losses || 5} consecutive losses`}. Entries paused.</div>
            </div>
          </div>
          <button
            data-testid="reset-loss-streak-risk-btn"
            onClick={handleResetStreak}
            disabled={resetting}
            className="px-2.5 py-1 text-[10px] font-bold rounded bg-amber-600 hover:bg-amber-500 active:bg-amber-700 text-white flex items-center gap-1 shadow shrink-0 border border-amber-400/40 transition-all font-mono uppercase"
          >
            <RotateCcw size={11} className={resetting ? "animate-spin" : ""} />
            {resetting ? "Resetting..." : resetSuccess ? "✅ Reset Done" : "Reset Streak"}
          </button>
        </div>
      )}

      {/* Progress Bars */}
      <div className="space-y-2.5">
        <Bar label="Daily Loss" value={risk.daily_loss_pct} max={risk.max_daily_loss_pct} isPercent={true} testId="daily-loss-bar" />
        <Bar label="Weekly Loss" value={risk.weekly_loss_pct} max={risk.max_weekly_loss_pct} isPercent={true} testId="weekly-loss-bar" />
        <Bar label="Drawdown" value={risk.drawdown_pct} max={risk.max_drawdown_pct} isPercent={true} testId="drawdown-bar" />
        <Bar label="Loss Streak" value={risk.consecutive_losses || 0} max={risk.loss_streak_pause_count || 5} isPercent={false} testId="loss-streak-bar" />
        <Bar label="Open Trades" value={risk.open_trades} max={risk.max_concurrent_trades || 50} isPercent={false} testId="open-trades-bar" />
        <Bar label="Open Orders" value={risk.open_orders || 0} max={risk.max_open_orders || 50} isPercent={false} testId="open-orders-bar" />
      </div>

      {/* Grid Stats */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-[10px] font-mono pt-3 border-t border-zinc-800/70 bg-zinc-900/20 p-2.5 rounded-lg border border-zinc-800/40">
        <div className="flex justify-between items-center"><span className="text-zinc-500">Exposure</span><span className="text-zinc-100 font-bold">${risk.exposure}</span></div>
        <div className="flex justify-between items-center"><span className="text-zinc-500">Max Lev</span><span className="text-zinc-100 font-bold">{risk.max_leverage}x</span></div>
        <div className="flex justify-between items-center"><span className="text-zinc-500">Risk / Trade</span><span className="text-zinc-100 font-bold">{risk.risk_per_trade_pct}%</span></div>
        <div className="flex justify-between items-center"><span className="text-zinc-500">Correlation</span><span className="text-zinc-100 font-bold">{risk.correlation_limit_enabled === false ? "disabled" : `≥ ${Number(risk.correlation_threshold ?? risk.max_position_correlation ?? 0).toFixed(2)} · ${risk.max_correlated_positions ?? 0} max`}</span></div>
        <div className="flex justify-between items-center"><span className="text-zinc-500">Quality Size</span><span className="text-zinc-100 font-bold">{risk.risk_quality_scaling_enabled ? `≥ ${risk.min_quality_risk_multiplier}x` : "base"}</span></div>
        <div className="flex justify-between items-center"><span className="text-zinc-500">Cooldown</span><span className="text-zinc-100 font-bold">{risk.cooldown_minutes}m</span></div>
        <div className="flex justify-between items-center"><span className="text-zinc-500">Free Margin</span><span className="text-zinc-100 font-bold">${Number(account?.available_margin || 0).toFixed(2)}</span></div>
        <div className="flex justify-between items-center"><span className="text-zinc-500">Margin Used</span><span className="text-zinc-100 font-bold">${Number(account?.used_margin || 0).toFixed(2)}</span></div>
        <div className="col-span-2 flex justify-between items-center pt-1 border-t border-zinc-800/40">
          <span className="text-zinc-400">Liquidation Buffer</span>
          <span className="text-emerald-400 font-bold text-[11px]">{Number(account?.liquidation_buffer || 0).toFixed(1)}%</span>
        </div>
      </div>
    </div>
  );
}
